import { Injectable, Inject, PLATFORM_ID, OnDestroy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { LogoutDetectionService } from './logout-detection.service';
import { Subject } from 'rxjs';

export interface InactivityConfig {
  /** Timeout in milliseconds (default: 15 minutes) */
  timeout?: number;
  /** Events to watch for activity (default: all) */
  events?: string[];
  /** Whether to enable multi-tab sync (default: true) */
  multiTabSync?: boolean;
  /** Whether to clear sessionStorage (default: true) */
  clearSessionStorage?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class InactivityService implements OnDestroy {
  private inactivityTimer: any;
  private readonly INACTIVITY_KEY = 'inactivity_logout_trigger';
  private readonly DEFAULT_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  private readonly DEFAULT_EVENTS = [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click',
    'keydown',
  ];

  private config: Required<InactivityConfig>;
  private storageListener?: (event: StorageEvent) => void;
  private eventListeners: Map<string, EventListener> = new Map();
  private lastActivityTime: number = 0;
  private readonly THROTTLE_MS = 1000; // Throttle activity checks to once per second

  // Observable for inactivity events
  private inactivitySubject = new Subject<void>();
  public onInactivity$ = this.inactivitySubject.asObservable();

  constructor(
    private router: Router,
    private authService: AuthService,
    private logoutDetectionService: LogoutDetectionService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    // Initialize with default config
    this.config = {
      timeout: this.DEFAULT_TIMEOUT,
      events: this.DEFAULT_EVENTS,
      multiTabSync: true,
      clearSessionStorage: true,
    };
  }

  /**
   * Configure inactivity detection settings
   * Call this before initialize() if you need custom settings
   */
  configure(config: InactivityConfig): void {
    this.config = {
      timeout: config.timeout ?? this.DEFAULT_TIMEOUT,
      events: config.events ?? this.DEFAULT_EVENTS,
      multiTabSync: config.multiTabSync ?? true,
      clearSessionStorage: config.clearSessionStorage ?? true,
    };
  }

  /**
   * Initialize inactivity detection
   */
  initialize(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Only initialize if user is logged in
    if (!this.isUserLoggedIn()) {
      return;
    }

    // Set up multi-tab sync
    if (this.config.multiTabSync) {
      this.setupMultiTabSync();
    }

    // Set up activity listeners
    this.setupActivityListeners();

    // Start the inactivity timer
    this.resetTimer();
  }

  /**
   * Check if user is logged in
   */
  private isUserLoggedIn(): boolean {
    const userData = localStorage.getItem('userData1');
    const userCode = localStorage.getItem('userCode');
    const eolUserCode = localStorage.getItem('eol_user_code');
    return !!(userData || userCode || eolUserCode);
  }

  /**
   * Set up activity event listeners
   */
  private setupActivityListeners(): void {
    // Remove existing listeners first to prevent duplicates
    this.removeActivityListeners();

    this.config.events.forEach((eventType) => {
      const listener = this.handleActivity.bind(this);
      document.addEventListener(eventType, listener, { passive: true });
      this.eventListeners.set(eventType, listener);
    });
  }

  /**
   * Remove activity event listeners
   */
  private removeActivityListeners(): void {
    this.eventListeners.forEach((listener, eventType) => {
      document.removeEventListener(eventType, listener);
    });
    this.eventListeners.clear();
  }

  /**
   * Handle user activity - reset the timer
   */
  private handleActivity(): void {
    const now = Date.now();

    // Throttle activity checks to prevent excessive timer resets
    if (now - this.lastActivityTime < this.THROTTLE_MS) {
      return;
    }

    this.lastActivityTime = now;

    // Only reset timer if user is still logged in
    if (this.isUserLoggedIn()) {
      this.resetTimer();
    }
  }

  /**
   * Reset the inactivity timer
   */
  private resetTimer(): void {
    // Clear existing timer
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }

    // Set new timer
    const timeoutSeconds = this.config.timeout / 1000;
    const logoutTime = new Date(Date.now() + this.config.timeout);
    this.inactivityTimer = setTimeout(() => {
      console.warn(
        `🚨 [InactivityService] Timer expired after ${timeoutSeconds} seconds of inactivity`,
      );
      this.handleInactivity();
    }, this.config.timeout);
  }

  /**
   * Handle inactivity timeout - perform logout
   */
  private handleInactivity(): void {
    // Double-check user is still logged in
    if (!this.isUserLoggedIn()) {
      return;
    }

    // Emit inactivity event
    this.inactivitySubject.next();

    // Clear all storage
    this.clearStorage();

    // Update auth state
    this.authService.setLoggedInState(false, null);

    // Trigger multi-tab sync if enabled
    if (this.config.multiTabSync) {
      this.triggerMultiTabLogout();
    }

    // Navigate to home
    this.router.navigate(['/home']).then(() => {
      // Optional: reload page to ensure clean state
      // Uncomment if needed:
      // setTimeout(() => {
      //   if (typeof window !== 'undefined') {
      //     window.location.reload();
      //   }
      // }, 100);
    });
  }

  /**
   * Clear localStorage and sessionStorage
   */
  private clearStorage(): void {
    // Clear all user-related localStorage items
    localStorage.removeItem('userData1');
    localStorage.removeItem('userData');
    localStorage.removeItem('useractive');
    localStorage.removeItem('userCode');
    localStorage.removeItem('eol_user_code');
    localStorage.removeItem('eol_ref_token');
    localStorage.removeItem('eol_access_token');
    localStorage.removeItem('registrationPhoneNumber');
    localStorage.removeItem('registrationOtp');

    // Clear sessionStorage if configured
    if (
      this.config.clearSessionStorage &&
      typeof sessionStorage !== 'undefined'
    ) {
      sessionStorage.clear();
    }
  }

  /**
   * Trigger logout in other tabs via localStorage event
   */
  private triggerMultiTabLogout(): void {
    try {
      const timestamp = Date.now().toString();
      localStorage.setItem(this.INACTIVITY_KEY, timestamp);
      // Remove immediately to allow future triggers
      setTimeout(() => {
        localStorage.removeItem(this.INACTIVITY_KEY);
      }, 100);
    } catch (error) {
      console.error('Error triggering multi-tab logout:', error);
    }
  }

  /**
   * Set up multi-tab synchronization
   */
  private setupMultiTabSync(): void {
    // Remove existing listener if any
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
    }

    this.storageListener = (event: StorageEvent) => {
      // Listen for inactivity logout trigger from other tabs
      if (event.key === this.INACTIVITY_KEY && event.newValue) {
        // Another tab triggered inactivity logout
        this.clearStorage();
        this.authService.setLoggedInState(false, null);
        this.router.navigate(['/home']);
      }

      // Also listen for existing logout detection keys
      if (
        event.key === 'userData1' ||
        event.key === 'userCode' ||
        event.key === 'eol_user_code'
      ) {
        if (!event.newValue && this.isUserLoggedIn()) {
          // User was logged out in another tab
          this.stop();
        }
      }
    };

    window.addEventListener('storage', this.storageListener);
  }

  /**
   * Manually reset the inactivity timer (useful after login)
   */
  reset(): void {
    if (this.isUserLoggedIn()) {
      this.resetTimer();
    }
  }

  /**
   * Stop inactivity detection
   */
  stop(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  /**
   * Restart inactivity detection (useful after login)
   */
  restart(): void {
    this.stop();
    // Clean up existing listeners before reinitializing
    this.removeActivityListeners();
    if (this.isUserLoggedIn()) {
      this.initialize();
    }
  }

  /**
   * Clean up resources
   */
  ngOnDestroy(): void {
    this.destroy();
  }

  /**
   * Destroy the service and clean up listeners
   */
  destroy(): void {
    this.stop();
    this.removeActivityListeners();

    // Remove storage listener
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = undefined;
    }
  }
}
